import React from 'react';
import moment from 'moment';

import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import { Event } from 'flow/types/Calendar';
import {
    ChannelKey,
    ContactKey,
    ExtensionKey,
    QueueKey,
    ReferKey,
} from 'flow/types/EntityKeys';
import { TypedMap } from 'flow/types/TypedMap';
import { isAllDayEvent } from 'flow/utils/calendar-utils';
import { twMerge } from 'tailwind-merge';
import Icon from '../ui-components/Icon';

interface Props {
    entityKey: ChannelKey | ContactKey | ExtensionKey | QueueKey | ReferKey;
    calendarEvent: TypedMap<Event>;
    editable: boolean;
}

const CalendarEvent = ({ entityKey, calendarEvent, editable }: Props) => {
    const { i18next } = useAppContext();
    const { openModal } = useModal();

    const getDayInterval = (calendarEvent: TypedMap<Event>) => {
        if (!calendarEvent.get('dayOffset')) {
            return null;
        }

        const day = calendarEvent.get('dayOffset');
        const interval = calendarEvent.get('dayInterval');

        const start = moment(calendarEvent.get('start')).format('HH:mm');
        const end = moment(calendarEvent.get('end')).format('HH:mm');

        const intervalString = i18next.t('calendar.event.occurence.day', {
            day: day,
            interval: interval,
        });

        if (day === 1) {
            return (
                i18next.t('calendar.start') + ' ' + start + ' ' + intervalString
            );
        } else if (day === interval) {
            return i18next.t('calendar.end') + ' ' + end + ' ' + intervalString;
        }

        return intervalString;
    };

    let title = calendarEvent.getIn(['profile', 'description']);

    if (calendarEvent.has('occurence')) {
        title += ' (' + i18next.t('calendar.reoccurring') + ')';
    }

    const start = moment(calendarEvent.get('start')).format('HH:mm');
    const eventEnd = calendarEvent.get('end');
    const end = eventEnd
        ? moment(eventEnd).format('HH:mm')
        : i18next.t('profilechooser.until_further_notice');

    let secondary = getDayInterval(calendarEvent);
    if (isAllDayEvent(calendarEvent)) {
        secondary = i18next.t('calendar.all.day');
    }

    if (start !== end && !calendarEvent.get('dayInterval')) {
        secondary = start + ' - ' + end;
    }

    return (
        <button
            className={twMerge(
                'flex items-center justify-between rounded-md bg-gray-100 p-3 text-gray-800',
                editable
                    ? 'pointer-events-auto'
                    : 'pointer-events-none cursor-default',
            )}
            onClick={() =>
                editable &&
                openModal('editCalendarEvent', {
                    entityKey,
                    calendarEvent,
                })
            }
        >
            <div>{title}</div>

            <div className={twMerge('flex items-center gap-x-2')}>
                <div className="flex text-sm">{secondary}</div>

                {editable && (
                    <Icon
                        iconKey="chevron_right"
                        className="rtl:scale-x-[-1]"
                    />
                )}
            </div>
        </button>
    );
};

export default CalendarEvent;
